import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import i18n from '../config/i18n';
import {
  BOOKING_GEO_COUNTRIES,
  type BookingGeoCountryCode,
} from '../config/geoLocale';
import { normalizeBookingLanguage } from '../config/i18n';
import {
  persistPreferredGeoCountry,
  readCachedGeoCountryOrFallback,
  resolveGeoCountry,
} from '../utils/detectGeoCountry';

interface GeoContextType {
  geoCountry: BookingGeoCountryCode;
  geoDetecting: boolean;
  changeGeoCountry: (code: BookingGeoCountryCode) => void;
  availableGeoCountries: typeof BOOKING_GEO_COUNTRIES;
}

const GeoContext = createContext<GeoContextType | undefined>(undefined);

export function GeoProvider({ children }: { children: ReactNode }) {
  const [geoCountry, setGeoCountryState] = useState<BookingGeoCountryCode>(() =>
    readCachedGeoCountryOrFallback(normalizeBookingLanguage(i18n.language))
  );
  const [geoDetecting, setGeoDetecting] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function detect() {
      setGeoDetecting(true);
      const lang = normalizeBookingLanguage(i18n.language);
      const resolved = await resolveGeoCountry(lang);
      if (!cancelled) {
        setGeoCountryState(resolved);
        setGeoDetecting(false);
      }
    }

    void detect();

    return () => {
      cancelled = true;
    };
  }, []);

  const changeGeoCountry = useCallback((code: BookingGeoCountryCode) => {
    persistPreferredGeoCountry(code);
    setGeoCountryState(code);
  }, []);

  return (
    <GeoContext.Provider
      value={{
        geoCountry,
        geoDetecting,
        changeGeoCountry,
        availableGeoCountries: BOOKING_GEO_COUNTRIES,
      }}
    >
      {children}
    </GeoContext.Provider>
  );
}

export function useGeo() {
  const ctx = useContext(GeoContext);
  if (!ctx) {
    throw new Error('useGeo must be used within GeoProvider');
  }
  return ctx;
}

/** Safe hors GeoProvider (modals / tests) — null si absent. */
export function useGeoOptional() {
  return useContext(GeoContext) ?? null;
}
